/**
 * Where a signed-OUT visitor's "Make offer" click is remembered, so a successful login can send
 * them back to the SAME published rider with the offer flow reopened
 * (`docs/V2_OFFER_TECH_APPROVAL.md`, PR2). `app-shell.ts`'s `resolveEffectiveRoute` reads this to
 * decide where `/login` sends a freshly-authenticated visitor; `pages/public-viewer-page.ts`
 * writes it (on click, while logged out) and consumes+clears it (on mount, once logged in).
 *
 * `localStorage`, not `sessionStorage` (unlike the guest draft, `editor/guest-draft.ts`): there is
 * no CONTENT to lose here, only a navigation intent, and the login round trip can legitimately
 * cross a tab boundary (a password manager, an emailed verification link opened elsewhere). A
 * tab-scoped store would silently drop the intent on exactly the browsers most likely to do that.
 *
 * Never throws: private browsing / a full quota / a locked-down embed degrade to "no intent
 * remembered" — the click still navigates to `/login`, it just loses the return trip.
 */
const KEY = "scb.offerReturnIntent";

export interface OfferReturnIntent {
  riderId: string;
  version: number;
  /** The show link the visitor came by (`?show=`), so the round trip still sends the offer for that show. */
  showToken?: string;
}

export function savePendingOfferIntent(intent: OfferReturnIntent): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(intent));
  } catch {
    /* see module doc comment — the click still reaches /login without a return trip */
  }
}

/** Read-only peek — does NOT clear the stored intent (`clearPendingOfferIntent` does that,
 *  separately, once the caller has actually acted on it). */
export function getPendingOfferIntent(): OfferReturnIntent | undefined {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as Partial<OfferReturnIntent>;
    if (typeof parsed.riderId !== "string" || parsed.riderId === "" || typeof parsed.version !== "number") {
      return undefined;
    }
    return { riderId: parsed.riderId, version: parsed.version, ...(typeof parsed.showToken === "string" && parsed.showToken !== "" ? { showToken: parsed.showToken } : {}) };
  } catch {
    return undefined; // corrupt/unavailable storage reads as "no intent", never throws
  }
}

export function clearPendingOfferIntent(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* nothing to clean up if storage was never reachable in the first place */
  }
}
